import React from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

type DrawerLink = {
  label: string;
  to: string;
};

export default function MobileDrawer({
  open,
  onClose,
  links,
  activePath
}: {
  open: boolean;
  onClose: () => void;
  links: DrawerLink[];
  activePath: string;
}) {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  React.useEffect(() => {
    if (open) onClose();
  }, [activePath, open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            className="fixed top-0 right-0 z-50 h-full w-72 bg-surface/95 border-l border-border-subtle shadow-glow"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
              <span className="font-mono text-xs tracking-[0.3em] text-text-subtle uppercase">
                menu
              </span>
              <button
                aria-label="Close menu"
                onClick={onClose}
                className="p-2 rounded-full border border-border-subtle hover:border-accent transition focus-visible:outline-none focus-visible:shadow-focus"
              >
                ✕
              </button>
            </div>
            <ul className="p-6 space-y-4">
              {links.map((link) => {
                const active = link.to === "/" ? activePath === "/" : activePath.startsWith(link.to);
                return (
                  <li key={link.to}>
                    <Link
                      onClick={onClose}
                      to={link.to}
                      className={`font-mono text-base tracking-wide block px-3 py-2 rounded-lg border transition ${
                        active ? "border-accent text-accent" : "border-transparent text-primary hover:text-accent"
                      }`}
                    >
                      {link.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
